import React from "react";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";

import MuiTable from "../../../components/MuiTable";
import useDeepMemo from "../../../hooks/useDeepMemo";
import { getDistinctValues, isEmpty, sizeKey } from "../../../utils/helpers";
import { findSizeBreakupList } from "../../../helpers/orderHelper";

import { MarkerType } from "../../../utils/types";
import { DEFAULT_SEPARATOR } from "../../../constants";

const NON_SB_FIELDS = ["width", "warp", "weft", "length", "efficiency"];

const parseRows = (rows, sbList, styleColorList) => {
  const markers = rows.map((m, idx) => {
    const sizeRatioList = sbList
      .map(({ size, sizeGroup, sizeKey: sKey, serialNo }) => ({
        ...(!isEmpty(m.sizeRatioList) ? m.sizeRatioList.find(sr => sr.sizeKey === sKey) : {}),
        serialNo,
        size,
        sizeGroup,
        sizeKey: sKey,
        ratio: m[sizeKey(sKey)]
      }))
      .filter(sr => sr.ratio && parseFloat(sr.ratio) > 0);

    const name = sizeRatioList.map(sr => `${sr.sizeKey}:${sr.ratio}`).join(", ");
    const noOfWays = sizeRatioList.map(sr => parseInt(sr.ratio)).reduce((acc, r) => acc + r, 0);
    const markerPlyList =
      m.markerPlyList ||
      styleColorList.map(styleColor => ({
        style: styleColor.split(DEFAULT_SEPARATOR)[0],
        color: styleColor.split(DEFAULT_SEPARATOR)[1],
        plannedPlies: 0,
        actualPlies: 0
      }));
    return { ...m, markerNo: idx + 1, name, sizeRatioList, noOfWays, markerPlyList };
  });

  return markers;
};

const ManualMarkerDialog = ({ open, order, orderFlow, onSubmit, onClose, onActionComplete }) => {
  const styleColorList = orderFlow?.styleColorList?.sort((a, b) => (a < b ? -1 : 1)) || [];

  const sizeBreakupList = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(orderFlow)) return [];
    return findSizeBreakupList(orderFlow, order);
  }, [order, orderFlow]);

  const { columns, headerRows } = useDeepMemo(() => {
    const sizeGroups = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup));
    const styleColorIndex = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});

    const groupedCutPlan = sizeGroups?.length > 1;

    let sizeGroup;
    let groupRow = {};
    const sizeList = sizeBreakupList.map((sb, idx) => {
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey(sb.sizeKey)] = sizeGroup;
      }
      return {
        dataKey: sizeKey(sb.sizeKey),
        title: sb.size,
        align: "center",
        styleColorIdx: sizeGroups.length > 1 ? styleColorIndex[sb.sizeGroup] : undefined,
        leftBorder,
        rightBorder: idx === sizeBreakupList.length - 1,
        headerCellProps: { style: { borderLeft: leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined } },
        colSpan: ({ column, row, rowIdx }) =>
          !groupedCutPlan || rowIdx === 1 || NON_SB_FIELDS.includes(column.dataKey)
            ? 1
            : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sizeBreakupList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
      };
    });

    let columnList = [
      ...sizeList,
      { dataKey: "width", title: "Width" },
      { dataKey: "warpPattern", title: "Warp Pattern" },
      { dataKey: "weftPattern", title: "Weft Pattern" },
      { dataKey: "length", title: "Length" },
      { dataKey: "efficiency", title: "Efficiency" }
    ];
    columnList = columnList.map(c => ({
      ...c,
      inputType: "text-input",
      align: "center",
      rowCellProps: { style: { width: c.dataKey.startsWith("s-") ? 80 : 100 } },
      options: { type: "number", style: { width: c.dataKey.startsWith("s-") ? 80 : 100 } }
    }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [groupRow, columnList.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    } else {
      headerRows = [columnList.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    }

    return { columns: columnList, headerRows };
  }, [order, orderFlow, sizeBreakupList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(orderFlow?.markerList)) return [{}, {}];
    let rowList = orderFlow.markerList
      .filter(m => m.markerType === MarkerType.RE_CUTTING)
      .sort((a, b) => a.markerNo - b.markerNo)
      .map(marker => {
        return {
          ...marker,
          ...marker.sizeRatioList?.reduce((acc, sr) => ({ ...acc, [sizeKey(sr.sizeKey)]: sr.ratio }), {}),
          ...marker.markerPlyList
            ?.sort((a, b) => (a.styleColor < b.styleColor ? -1 : 1))
            .reduce((acc, mp, idx) => ({ ...acc, [`plies-${idx}`]: mp.plannedPlies }), {}),
          groupId: marker.group?.id
        };
      });
    return rowList;
  }, [orderFlow]);

  const onValidate = values => {
    const markers = parseRows(values.rows, sizeBreakupList, styleColorList);
    const errors = markers.map(m => {
      let error = {};
      if (m.noOfWays <= 0) {
        error[sizeKey(sizeBreakupList[0]?.sizeKey)] = "Required";
      }
      if (isEmpty(m.length)) {
        error.length = "Required";
      }
      if (!isEmpty(m.length) && parseFloat(m.length) < 0) {
        error.length = "Must be positive value";
      }
      if (isEmpty(m.efficiency)) {
        error.efficiency = "Required";
      }
      if (!isEmpty(m.efficiency) && parseFloat(m.efficiency) < 0) {
        error.efficiency = "Must be positive value";
      }
      if (!isEmpty(m.efficiency) && parseFloat(m.efficiency) > 100) {
        error.efficiency = "Must be less than 100";
      }
      if (isEmpty(m.width)) {
        error.width = "Required";
      }
      if (isEmpty(m.warpPattern) && !isEmpty(m.weftPattern)) {
        error.warpPattern = "Required";
      }
      if (!isEmpty(m.warpPattern) && isEmpty(m.weftPattern)) {
        error.weftPattern = "Required";
      }

      return error;
    });

    const error = { rows: errors };
    return error;
  };

  const handleSubmit = values => {
    const markers = parseRows(values, sizeBreakupList, styleColorList);
    onSubmit && onSubmit(markers, onActionComplete);
  };

  const onFooterActionClick = (event, action, rows) => {
    if (action === "cancel") {
      onClose && onClose();
    }
  };

  return (
    <Dialog id="adjust-dialog" fullWidth={true} maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle id="import-dialog-title">Manual Markers</DialogTitle>
      <MuiTable
        headerCellStyle={{ backgroundColor: "#C6D0DA" }}
        columns={columns}
        headerRows={headerRows}
        rows={rows}
        fontSize={16}
        editable
        editing
        showEditableActions
        rowAddCount={1}
        // actionPlacement="left"
        variant="excel"
        validate={onValidate}
        onSubmit={handleSubmit}
        onFooterActionClick={onFooterActionClick}
      />
    </Dialog>
  );
};

export default ManualMarkerDialog;
